import "./App.css";
import React, { useEffect, useState, Suspense, lazy } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css'; // Import styles!
import { Routes, Route } from "react-router-dom";
import CookieConsent from "react-cookie-consent";
import Header from "./Components/Header";
import { MenuProvider } from "./MenuContext";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useLocation } from 'react-router-dom';
import BestBudget2025 from "./Components/Blogs/BestBudget2025";
import ZeroBudget2026 from "./Components/Blogs/ZeroBudget2026";


// Lazy Load Components
const LandingWebsite = lazy(() => import("./Components/LandingWebsite"));
const BestBudgetWay = lazy(() => import("./Components/BestBudgetWay"));
const Pricing = lazy(() => import("./Components/Pricing"));
const Learn = lazy(() => import("./Components/Learn"));
const AboutUs = lazy(() => import("./Components/AboutUs"));
const Blog = lazy(() => import("./Components/Blog"));
const PrivacyPolicy = lazy(() => import("./Components/Legal/PrivacyPolicy"));
const ServiceTerms = lazy(() => import("./Components/Legal/ServiceTerms"));
const Security = lazy(() => import("./Components/Legal/Security"));
const CookiesPolicy = lazy(() => import("./Components/Legal/CookiesPolicy"));
const LearningCenter = lazy(() => import("./Components/Learn/LearningCenter"));
const FinancialPlanning = lazy(() => import("./Components/Blogs/FinancialPlanning"));
const BudgetingBlog = lazy(() => import("./Components/Blogs/BudgetingBlog"));
const InvestingBlog = lazy(() => import("./Components/Blogs/InvestingBlog"));
const SavingBlog = lazy(() => import("./Components/Blogs/SavingBlog"));
const QuickAccessSignUpLogin = lazy(() => import("./Components/Learn/Videos/QuickAccessSignUpLogin"));
const FirstStepsSettingUpBestBudget = lazy(() => import("./Components/Learn/Videos/FirstStepsSettingUpBestBudget"));
const OrgainiseYourFinancesByAddingAccounts = lazy(() => import("./Components/Learn/Videos/OrgainiseYourFinancesByAddingAccounts"));
const ManagingBudgetCategories = lazy(() => import("./Components/Learn/Videos/ManagingBudgetCategories"));
const TrackManagePayeesAndTransactions = lazy(() => import("./Components/Learn/Videos/TrackManagePayeesAndTransactions"));
const YourFinancialOverviewDashboardReports = lazy(() => import("./Components/Learn/Videos/YourFinancialOverviewDashboardReports"));
const ManageBudgetsAndPersonalizeSettings = lazy(() => import("./Components/Learn/Videos/ManageBudgetsAndPersonalizeSettings"));
const DeleteAccount = lazy(() => import("./Components/Learn/Profile/DeleteAccount"));

const App = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const canonicalUrl = `https://www.bestbudget.in${location.pathname}`.replace(/\/$/, '');

const isMobile =
  typeof window !== "undefined"
    ? window.innerWidth <= 440
    : false;
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000); // 2 seconds fake loading

    return () => clearTimeout(timer);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openExternalLink = (url) => {
    window.open(url, "_blank"); // Opens in a new tab
  };

  const helmetContext = {};

  if (loading) {
    return (
      <div style={{ padding: "20px" }}>
        <Skeleton height={60} width={300} />
        <Skeleton height={400} width="100%" style={{ marginTop: 20 }} />
        <Skeleton height={30} width="60%" style={{ marginTop: 20 }} />
        <Skeleton height={30} width="40%" style={{ marginTop: 10 }} />
      </div>
    );
  }

  return (
    <MenuProvider>
      <div className="App">
        <Header />
        <div className="browser-router-container">
          <HelmetProvider context={helmetContext}>
            <Helmet>
              <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            <Suspense fallback={<Skeleton height={60} width={300} />}>
              <Routes>
                <Route
                  path="/"
                  element={<LandingWebsite scrollToTop={scrollToTop} openExternalLink={openExternalLink} />}
                />
                <Route
                  path="/why-best-budget"
                  element={<BestBudgetWay scrollToTop={scrollToTop} openExternalLink={openExternalLink} />}
                />
                <Route
                  path="/pricing"
                  element={<Pricing scrollToTop={scrollToTop} openExternalLink={openExternalLink} />}
                />
                <Route path="/learn" element={<Learn scrollToTop={scrollToTop} />} />
                <Route path="/about-us" element={<AboutUs scrollToTop={scrollToTop} />} />
                <Route path="/blog" element={<Blog />} />
                <Route
                  path="/privacy-policy"
                  element={<PrivacyPolicy scrollToTop={scrollToTop} />}
                />
                <Route
                  path="/service-terms"
                  element={<ServiceTerms scrollToTop={scrollToTop} />}
                />
                <Route path="/security" element={<Security scrollToTop={scrollToTop} />} />
                <Route
                  path="/cookies-policy"
                  element={<CookiesPolicy scrollToTop={scrollToTop} />}
                />
                <Route
                  path="/learn/learning-center/*"
                  element={<LearningCenter scrollToTop={scrollToTop} />}
                />  <Route
                  path="/learn/learning-center/"
                  element={<LearningCenter scrollToTop={scrollToTop} />}
                />
                <Route
                  path="/blog/need-to-achieve-good-outcome-from-financial-planning"
                  element={<FinancialPlanning openExternalLink={openExternalLink} />}
                />
                <Route
                  path="/blog/how-budgeting-drives-financial-success-and-management"
                  element={<BudgetingBlog openExternalLink={openExternalLink} />}
                />
                <Route
                  path="/blog/top-tips-for-tracking-all-your-investments-in-one-place"
                  element={<InvestingBlog openExternalLink={openExternalLink} />}
                />
                <Route
                  path="/blog/effective-tips-for-saving-money-quickly-and-easily"
                  element={<SavingBlog openExternalLink={openExternalLink} />}
                />
                <Route
                  path="/blog/Smart-Personal-Finance-Management-in-2025"
                  element={<BestBudget2025 openExternalLink={openExternalLink} />}
                />
                <Route
                  path="/blog/zero-based-budgeting-the-complete-2026-guide"
                  element={<ZeroBudget2026 openExternalLink={openExternalLink} />}
                />
                <Route
                  path="/learn/videos/quick-access-sign-up-login"
                  element={<QuickAccessSignUpLogin />}
                />
                <Route
                  path="/learn/videos/your-first-steps-setting-up-best-budget-like-a-pro"
                  element={<FirstStepsSettingUpBestBudget />}
                />
                <Route
                  path="/learn/videos/organize-your-finances-adding-managing-accounts"
                  element={<OrgainiseYourFinancesByAddingAccounts />}
                />
                <Route
                  path="/learn/videos/managing-your-budget-categories-setting-budget-allocations"
                  element={<ManagingBudgetCategories />}
                />
                <Route
                  path="/learn/videos/track-manage-payments-payees-transactions-made-simple"
                  element={<TrackManagePayeesAndTransactions />}
                />
                <Route
                  path="/learn/videos/your-financial-overview-dashboard-reports-explained"
                  element={<YourFinancialOverviewDashboardReports />}
                />
                <Route
                  path="/learn/videos/manage-budgets-and-personalize-settings"
                  element={<ManageBudgetsAndPersonalizeSettings />}
                />
                <Route
                  path="/learn/learning-center/profile-delete-account"
                  element={<DeleteAccount />}
                />
              </Routes>
            </Suspense>
          </HelmetProvider>
        </div>
        <CookieConsent
          className="cookies-consent-container"
          location="bottom"
          buttonText="Accept All"
          declineButtonText="Decline"
          enableDeclineButton
          cookieName="CREXIN Technologies"
          style={{
            background: "#1080e3",
           
            left: isMobile ? "auto" : "0px",
            textAlign: isMobile ? "left" : "center",
            fontSize: isMobile ? "16px" : "12px",
            margin: isMobile ? "0px 50px" : "0px auto",
            justifyContent: "center", // Center buttons horizontally
            alignItems: "center",
          }}
          buttonStyle={{
            background: "#47AF3F",
            color: "#FFFFFF",
            fontSize: isMobile ? "16px" : "12px",
            borderRadius: "5px",
            margin: isMobile ? "20px 20px" : "10px 10px",
          }}
          declineButtonStyle={{
            fontSize: isMobile ? "16px" : "12px",
            borderRadius: "5px",
            margin: isMobile ? "20px 20px" : "10px 10px",
          }}
          expires={150}
          onAccept={() => {
            console.log("Cookies accepted");
          }}
          onDecline={() => {
            console.log("Cookies declined");
          }}
        >
          We use cookies to improve your experience. By continuing to use our
          website, you agree to our use of cookies.{" "}
          <a href="/cookies-policy" style={{ color: "#FFD700" }}>
            Learn More
          </a>
        </CookieConsent>
      </div>
    </MenuProvider>
  );
};

export default App;
